import * as z from 'zod';
import '../../../financial-formula/adjustable-value.mjs';
import '../../../financial-formula/adjustable-values-update.mjs';
import { Adjustment } from '../../../financial-formula/adjustment.mjs';
import '../../../financial-formula/formula.mjs';
import '@inrev/core';
import { currencySchema } from '../../../utils.mjs';
import { currentAssetKeys, fixedAssetKeys, nonCurrentAssetKeys, currentLiabilityKeys, nonCurrentLiabilityKeys, equityKeys } from './section-keys.mjs';

var AdjustedLeaf;
(function (AdjustedLeaf) {
    AdjustedLeaf.schema = Adjustment.schema.extend({
        value: currencySchema,
    });
})(AdjustedLeaf || (AdjustedLeaf = {}));
var LeafView;
(function (LeafView) {
    LeafView.schema = z.object({
        output: currencySchema.optional(),
        given: currencySchema.optional(),
        adjusted: AdjustedLeaf.schema.optional(),
        allowed: currencySchema.optional(),
    });
})(LeafView || (LeafView = {}));
var RollupView;
(function (RollupView) {
    RollupView.schema = z.object({
        output: currencySchema.optional(),
        given: currencySchema.optional(),
        adjusted: currencySchema.optional(),
        allowed: currencySchema.optional(),
    });
})(RollupView || (RollupView = {}));
var LineItemView;
(function (LineItemView) {
    LineItemView.schema = LeafView.schema.extend({
        label: z.string(),
        autoSource: z.string().optional(),
    });
})(LineItemView || (LineItemView = {}));
var LineItemGroupView;
(function (LineItemGroupView) {
    LineItemGroupView.schema = z.object({
        total: RollupView.schema,
        lineItems: z.array(LineItemView.schema),
    });
})(LineItemGroupView || (LineItemGroupView = {}));
const sectionSchemaFromKeys = (keys) => z.object({
    ...Object.fromEntries(keys.map((k) => [k, LineItemGroupView.schema])),
    total: RollupView.schema,
});
var CurrentAssetsView;
(function (CurrentAssetsView) {
    CurrentAssetsView.schema = sectionSchemaFromKeys(currentAssetKeys);
})(CurrentAssetsView || (CurrentAssetsView = {}));
var FixedAssetsView;
(function (FixedAssetsView) {
    FixedAssetsView.schema = sectionSchemaFromKeys(fixedAssetKeys);
})(FixedAssetsView || (FixedAssetsView = {}));
var NonCurrentAssetsView;
(function (NonCurrentAssetsView) {
    NonCurrentAssetsView.schema = sectionSchemaFromKeys(nonCurrentAssetKeys);
})(NonCurrentAssetsView || (NonCurrentAssetsView = {}));
var CurrentLiabilitiesView;
(function (CurrentLiabilitiesView) {
    CurrentLiabilitiesView.schema = sectionSchemaFromKeys(currentLiabilityKeys);
})(CurrentLiabilitiesView || (CurrentLiabilitiesView = {}));
var NonCurrentLiabilitiesView;
(function (NonCurrentLiabilitiesView) {
    NonCurrentLiabilitiesView.schema = sectionSchemaFromKeys(nonCurrentLiabilityKeys);
})(NonCurrentLiabilitiesView || (NonCurrentLiabilitiesView = {}));
var EquityView;
(function (EquityView) {
    EquityView.schema = sectionSchemaFromKeys(equityKeys);
})(EquityView || (EquityView = {}));
var AssetsView;
(function (AssetsView) {
    AssetsView.schema = z.object({
        current: CurrentAssetsView.schema,
        fixed: FixedAssetsView.schema,
        nonCurrent: NonCurrentAssetsView.schema,
        total: RollupView.schema,
    });
})(AssetsView || (AssetsView = {}));
var LiabilitiesView;
(function (LiabilitiesView) {
    LiabilitiesView.schema = z.object({
        current: CurrentLiabilitiesView.schema,
        nonCurrent: NonCurrentLiabilitiesView.schema,
        total: RollupView.schema,
    });
})(LiabilitiesView || (LiabilitiesView = {}));
var BalanceSheetView;
(function (BalanceSheetView) {
    BalanceSheetView.schema = z.object({
        assets: AssetsView.schema,
        liabilities: LiabilitiesView.schema,
        equity: EquityView.schema,
    });
})(BalanceSheetView || (BalanceSheetView = {}));
var IncomeStatementView;
(function (IncomeStatementView) {
    IncomeStatementView.schema = z.object({
        revenue: LineItemGroupView.schema,
        cogs: LeafView.schema,
        grossProfit: RollupView.schema,
        operatingExpenses: LeafView.schema,
        operatingProfit: RollupView.schema,
        otherIncome: LeafView.schema,
        otherExpenses: LeafView.schema,
        taxes: LeafView.schema,
        netProfit: RollupView.schema,
        depreciation: LeafView.schema,
        amortization: LeafView.schema,
        depreciationAndAmortization: LeafView.schema,
        interestExpense: LeafView.schema,
        incomeTaxes: LeafView.schema,
        irregularGainLossFromAssetSale: LeafView.schema,
        distributions: LeafView.schema,
    });
})(IncomeStatementView || (IncomeStatementView = {}));

export { AdjustedLeaf, AssetsView, BalanceSheetView, CurrentAssetsView, CurrentLiabilitiesView, EquityView, FixedAssetsView, IncomeStatementView, LeafView, LiabilitiesView, LineItemGroupView, LineItemView, NonCurrentAssetsView, NonCurrentLiabilitiesView, RollupView };
